import { useRef, useState } from 'react';
import { uploadProductsCsv, confirmProductsCsv } from '../../services/supplierService';
import './CsvUploadModal.css';

const TEMPLATE_HEADERS = [
  'product_id','supplier_id','source_type','document_name_reference','product_status',
  'supplier_name','supplier_product_name','molecule_name','combines_molecules','molecule_comments',
  'strength_value_1','strength_unit_code_1','dosage_form','pack_size','atc_code',
  'atc_classification_name','atc_category','market_auth_countries','full_strength',
  'therapeutic_indications','inn','manufacturing_sites','market_auth_status','patent_expiry_date',
  'patent_status','patent_notes','supplier_availability','reference_product_name',
  'dossier_availability_status','dossier_preparation_planned_date','therapeutical_indication',
  'additional_comments',
];

export default function CsvUploadModal({ onClose, onDone, onToast }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const downloadTemplate = () => {
    const csv = TEMPLATE_HEADERS.join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'supplier-products-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const parseFile = async (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      onToast({ type: 'error', msg: 'Please upload a .csv file' });
      return;
    }
    setFile(f);
    setLoading(true);
    setResult(null);
    try {
      const data = await uploadProductsCsv(f);
      setResult(data);
    } catch (err) {
      onToast({ type: 'error', msg: err?.response?.data?.error || 'CSV upload failed' });
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const onPickFile = (e) => parseFile(e.target.files && e.target.files[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    parseFile(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  const confirm = async () => {
    if (!result || !Array.isArray(result.rows) || result.rows.length === 0) return;
    setConfirming(true);
    try {
      const res = await confirmProductsCsv(result.rows, 'CSV Upload');
      onToast({ type: 'ok', msg: `${res.inserted} products added successfully.` });
      onDone();
    } catch (err) {
      onToast({ type: 'error', msg: err?.response?.data?.error || 'CSV confirm failed' });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="sup-modal-backdrop" onClick={onClose}>
      <div className="sup-modal csv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head">
          <h5 className="sup-modal-title mb-0">
            <span className="sup-title-main">Add New Products via CSV</span>
          </h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="sup-modal-body">
          <p className="csv-intro">
            Upload a CSV file to bulk-add supplier products. Download the template below,
            fill in your data, and upload.
          </p>

          <button type="button" className="sup-btn mb-3" onClick={downloadTemplate}>
            <i className="bi bi-download me-1"></i> Download Template
          </button>

          <div
            className={`csv-drop${dragOver ? ' over' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onPickFile}
              style={{ display: 'none' }}
            />
            {loading ? (
              <div className="csv-loading">
                <span className="csv-spinner" /> Parsing file…
              </div>
            ) : file ? (
              <>
                <i className="bi bi-file-earmark-text" style={{ fontSize: 32 }}></i>
                <div className="csv-file-name">{file.name}</div>
                <div className="csv-hint">Click or drop another file to replace</div>
              </>
            ) : (
              <>
                <i className="bi bi-cloud-arrow-up" style={{ fontSize: 32 }}></i>
                <div>Drop CSV here or click to select</div>
                <div className="csv-hint">.csv only</div>
              </>
            )}
          </div>

          {result && (
            <div className="csv-result">
              <div className="csv-summary">
                <strong>{result.valid}</strong> rows ready to upload.
                &nbsp;<strong>{result.duplicates}</strong> duplicates skipped.
              </div>
              {result.preview && result.preview.length > 0 && (
                <div className="csv-preview-wrap">
                  <div className="csv-preview-title">Preview (first 5 rows)</div>
                  <div className="csv-preview-scroll">
                    <table className="csv-preview-table">
                      <thead>
                        <tr>
                          <th>supplier_name</th>
                          <th>supplier_product_name</th>
                          <th>inn</th>
                          <th>dosage_form</th>
                          <th>strength_value_1</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.preview.map((r, i) => (
                          <tr key={i}>
                            <td>{r.supplier_name || '—'}</td>
                            <td>{r.supplier_product_name || '—'}</td>
                            <td>{r.inn || '—'}</td>
                            <td>{r.dosage_form || '—'}</td>
                            <td>{r.strength_value_1 || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onClose} disabled={confirming}>
            <i className="bi bi-x-circle me-1"></i> Cancel
          </button>
          <button
            type="button"
            className="sup-btn primary"
            onClick={confirm}
            disabled={!result || result.valid === 0 || confirming}
          >
            <i className="bi bi-check-circle me-1"></i>
            {confirming ? 'Uploading…' : `Confirm & Upload${result ? ` (${result.valid})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
